import { HttpErrorResponse } from '@angular/common/http';

/**
 * Base class for domain errors. Owns the transport-layer `HttpErrorResponse` as `source`
 * (composition, not inheritance) and carries everything a consumer needs:
 * - `translationKey` — i18n key to display
 * - `code` — BE error code (explicit, or auto-extracted from `source.error.code`)
 * - `data` — typed domain-specific payload (e.g. conflicting rules)
 *
 * Errors are created only in business services (`*.service.ts`), inside `catchError`.
 * Use `AppError.from(err, 'key')` for simple cases, or a domain subclass when consumers
 * need `instanceof` checks or extra data.
 */
export class AppError<TData = never> extends Error {
  public readonly statusCode: number;
  public readonly code: string | null;
  public readonly translationKey: string;
  public readonly source: HttpErrorResponse;
  public readonly data: TData | undefined;

  constructor(source: HttpErrorResponse, translationKey: string, code?: string | null, data?: TData) {
    super(translationKey);
    this.name = new.target.name;
    this.statusCode = source.status;
    this.code = code ?? (source.error?.code as string | null) ?? null;
    this.translationKey = translationKey;
    this.source = source;
    this.data = data;
  }

  public static from(source: HttpErrorResponse, translationKey: string): AppError {
    return new AppError(source, translationKey);
  }

  /**
   * Appends the HTTP status to an operation-level key (e.g. `api-errors.did.get` →
   * `api-errors.did.get.404`). The `ApiErrorMissingTranslationHandler` resolves the
   * per-status/`default`/`general` fallback, so callers never enumerate every status.
   */
  public static fromApi(source: HttpErrorResponse, operationKey: string): AppError {
    return new AppError(source, `${operationKey}.${source.status}`);
  }
}
